"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrdersClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const commons_logs_1 = require("@deliverr/commons-logs");
const errors_1 = require("@deliverr/errors");
const business_types_1 = require("@deliverr/business-types");
const OrderException_1 = require("./OrderException");
const ReimbursementData_1 = require("./ReimbursementData");
const ORDERS_URL = process.env.FULFILLMENT_SERVICE_URL;
const ORDERS_BASIC_AUTH_USER = process.env.ORDERS_BASIC_AUTH_USER;
const ORDERS_BASIC_AUTH_PASSWORD = process.env.ORDERS_BASIC_AUTH_PASSWORD;
class OrdersClient extends base_client_1.ApiBasicAuthClient {
    constructor(config = {}) {
        super(Object.assign(Object.assign({}, config), { baseURL: `${config.baseURL || ORDERS_URL}/`, basicAuthPassword: ORDERS_BASIC_AUTH_PASSWORD, basicAuthUser: ORDERS_BASIC_AUTH_USER }));
        if (!ORDERS_BASIC_AUTH_USER || !ORDERS_BASIC_AUTH_PASSWORD) {
            commons_logs_1.log.warn("ORDERS_BASIC_AUTH_USER and ORDERS_BASIC_AUTH_PASSWORD environment variable is not set");
        }
    }
    async getBulkOrderPreview(signedUrl) {
        return this.get(`/v1/orders/bulk/preview?signedUrl=${signedUrl}`);
    }
    async importBulkOrder(signedUrl, sellerId) {
        return this.post(`/v1/orders/bulk`, {}, { sellerId, signedUrl });
    }
    async getShipment(shipmentId) {
        try {
            return await this.get(`/v1/workflow/shipment/${shipmentId}`);
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 404:
                    return undefined;
                case 504:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.NETWORK_ERROR,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.UNKNOWN_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async getCarrierByTrackingCode(trackingCode) {
        try {
            return await this.get(`/v1/package/trackingCode/${trackingCode}/carrier`);
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 404:
                    return undefined;
                case 504:
                    throw new errors_1.DeliverrError(errors_1.ApiError.NETWORK_ERROR);
                default:
                    throw new errors_1.DeliverrError(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async updateTrackingByTrackingCode(trackingCode, trackingSummary) {
        try {
            await this.post("/v1/package/tracking-update", {
                trackingCode,
                trackingSummary,
            });
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 404:
                    throw new errors_1.DeliverrError(business_types_1.OrderExceptions.NOT_FOUND);
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.UNKNOWN_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async getShipmentReimbursement(shipmentId) {
        try {
            return await this.get(`/v1/workflow/reimbursement/${shipmentId}`);
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 400:
                    throw new errors_1.DeliverrError({
                        code: ReimbursementData_1.ShipmentReimbursementExceptions.SHIPMENT_ID_REQUIRED,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.UNKNOWN_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async createShipmentReimbursement(shipmentId, reimbursementRate) {
        var _a;
        try {
            return await this.post(`/v1/workflow/reimbursement/${shipmentId}`, {
                reimbursementRate,
            });
        }
        catch (error) {
            switch ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) {
                case 400:
                case 404:
                case 503:
                    throw (0, base_client_1.mapError)(error);
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.UNKNOWN_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async updatePackageTrackingSummary(trackingCode, trackingSummary) {
        try {
            await this.put(`/v1/package/update-tracking-summary/${trackingCode}`, {
                trackingSummary,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async getOrder(orderId) {
        return this.getOrderSummary(orderId);
    }
    async getOrderSummary(orderId) {
        try {
            return await this.get(`/v1/workflow/order/${orderId}`);
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 404:
                    commons_logs_1.log.error("getOrderSummary: not found", orderId);
                    return undefined;
                case 504:
                    commons_logs_1.log.error(errors_1.ApiError.NETWORK_ERROR);
                    throw new Error(errors_1.ApiError.NETWORK_ERROR);
                default:
                    commons_logs_1.log.error("getOrderSummary: unknown error", orderId, error.response);
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async getDeliverrOrder(orderId) {
        try {
            return await this.get(`/v1/order/${orderId}`);
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 404:
                    commons_logs_1.log.error("getDeliverrOrder: not found", orderId);
                    return undefined;
                case 504:
                    commons_logs_1.log.error(errors_1.ApiError.NETWORK_ERROR);
                    throw new Error(errors_1.ApiError.NETWORK_ERROR);
                default:
                    commons_logs_1.log.error("getDeliverrOrder: unknown error", orderId, error.response);
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async getOrderByMarketplaceOrderId(marketplaceOrderId, sellerId) {
        try {
            return await this.get("/v1/workflow/order", {
                marketplaceOrderId,
                sellerId,
            });
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 400:
                    throw new errors_1.DeliverrError({
                        code: business_types_1.OrderExceptions.INVALID_QUERY_PARAMETER,
                        payload: error,
                    });
                case 404:
                    return undefined;
                case 504:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.NETWORK_ERROR,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.UNKNOWN_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async getOrderByTrackingCode(trackingCode) {
        try {
            return await this.get(`/v1/workflow/order/tracking-code/${trackingCode}`);
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 404:
                    return undefined;
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.UNKNOWN_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async createOrders(orders) {
        try {
            return await this.post("/v1/orders", { orders });
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 504:
                    commons_logs_1.log.error(errors_1.ApiError.NETWORK_ERROR);
                    throw new Error(errors_1.ApiError.NETWORK_ERROR);
                default:
                    commons_logs_1.log.error("createOrders: unknown error", orders, error.response);
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async createOrder(order) {
        try {
            return await this.post("/v1/order", order);
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 400:
                    commons_logs_1.log.error("createOrder: Invalid address", order, error.response);
                    throw new Error(business_types_1.OrderExceptions.INVALID_ADDRESS);
                case 409:
                    commons_logs_1.log.error("createOrder: Duplicate order", order, error.response);
                    throw new Error(business_types_1.OrderExceptions.DUPLICATE_ORDER);
                case 410:
                    commons_logs_1.log.error("createOrder: Stockout", order, error.response);
                    throw new Error(business_types_1.OrderExceptions.STOCKOUT);
                case 504:
                    commons_logs_1.log.error(errors_1.ApiError.NETWORK_ERROR);
                    throw new Error(errors_1.ApiError.NETWORK_ERROR);
                default:
                    commons_logs_1.log.error("createOrder: unknown error", order, error.response);
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async createOrderV2(order) {
        try {
            return await this.post("/v2/order", order);
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 400:
                    commons_logs_1.log.error("createOrder: Invalid seller", order, error.response);
                    throw new OrderException_1.OrderException(business_types_1.OrderExceptions.INVALID_SELLER, error);
                case 409:
                    commons_logs_1.log.error("createOrder: Duplicate order", order, error.response);
                    throw new OrderException_1.OrderException(business_types_1.OrderExceptions.DUPLICATE_ORDER, error);
                case 410:
                    commons_logs_1.log.error("createOrder: Stockout", order, error.response);
                    throw new OrderException_1.OrderException(business_types_1.OrderExceptions.STOCKOUT, error);
                case 422:
                    commons_logs_1.log.error("createOrder: Unknown sku", order, error.response);
                    throw new OrderException_1.OrderException(business_types_1.OrderExceptions.UNKNOWN_SKU, error);
                case 504:
                    commons_logs_1.log.error(errors_1.ApiError.NETWORK_ERROR);
                    throw new OrderException_1.OrderException(errors_1.ApiError.NETWORK_ERROR, error);
                default:
                    const errorType = error.response.data.error || errors_1.ApiError.UNKNOWN_ERROR;
                    commons_logs_1.log.error(`createOrder: ${errorType}`, order, error.response);
                    throw new OrderException_1.OrderException(errorType, error);
            }
        }
    }
    async updateShippingAddress(orderId, address, forceUpdate = false) {
        try {
            const body = { address, forceUpdate };
            return await this.put(`/v1/order/${orderId}/address`, body);
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 400:
                    commons_logs_1.log.error("updateShippingAddress: order already shipped", orderId, forceUpdate, error.response);
                    throw new Error(business_types_1.OrderExceptions.ORDER_SHIPPED);
                case 404:
                    commons_logs_1.log.error("updateShippingAddress: order not found", orderId, forceUpdate, error.response);
                    throw new Error(business_types_1.OrderExceptions.NOT_FOUND);
                case 422:
                    commons_logs_1.log.error("updateShippingAddress: invalid address provided", orderId, forceUpdate, error.response);
                    throw new Error(business_types_1.OrderExceptions.INVALID_ADDRESS);
                case 504:
                    commons_logs_1.log.error(errors_1.ApiError.NETWORK_ERROR);
                    throw new Error(errors_1.ApiError.NETWORK_ERROR);
                default:
                    commons_logs_1.log.error("updateShippingAddress: unknown error", orderId, forceUpdate, error.response);
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async cancelOrder(orderId, cancelSource) {
        try {
            return await this.delete(`/v1/workflow/order/${orderId}`, undefined, {
                cancelSource,
            });
        }
        catch (error) {
            if (error.response.data && error.response.data.error === business_types_1.OrderExceptions.FAILED_CANCEL_AT_WAREHOUSE) {
                commons_logs_1.log.error("cancelOrder: failed to cancel at warehouse");
                throw new Error(business_types_1.OrderExceptions.FAILED_CANCEL_AT_WAREHOUSE);
            }
            switch (error.response && error.response.status) {
                case 400:
                    commons_logs_1.log.error("cancelOrder: order already shipped or delivered", orderId);
                    throw new Error(business_types_1.OrderExceptions.ORDER_SHIPPED);
                case 404:
                    commons_logs_1.log.error("cancelOrder: order not found", orderId);
                    throw new Error(business_types_1.OrderExceptions.NOT_FOUND);
                case 504:
                    commons_logs_1.log.error(errors_1.ApiError.NETWORK_ERROR);
                    throw new Error(errors_1.ApiError.NETWORK_ERROR);
                default:
                    commons_logs_1.log.error("cancelOrder: unknown error", orderId);
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async updateStatus(orderId, maxPackageDeliverTime) {
        try {
            await this.put("/v1/order/update-status", {
                orderId,
                maxPackageDeliverTime,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async retryOrderProcessing(orderId) {
        try {
            return await this.post(`/v1/order/${orderId}/retry`, {});
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 404:
                    throw new errors_1.DeliverrError({
                        code: business_types_1.OrderExceptions.NOT_FOUND,
                        payload: error,
                    });
                case 422:
                    throw new errors_1.DeliverrError({
                        code: business_types_1.OrderExceptions.INVALID_ORDER_STATUS,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.NETWORK_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async updateOrder(orderId, address, items) {
        var _a, _b;
        try {
            return await this.put(`/v1/order/${orderId}`, {
                address,
                items,
            });
        }
        catch (error) {
            switch ((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) {
                case 404:
                    throw new errors_1.DeliverrError({
                        code: business_types_1.OrderExceptions.NOT_FOUND,
                        payload: error,
                    });
                case 422:
                    throw new errors_1.DeliverrError({
                        code: (_b = error.response.data.error) !== null && _b !== void 0 ? _b : business_types_1.OrderExceptions.INVALID_ORDER_STATUS,
                        payload: error,
                    });
                default:
                    throw new errors_1.DeliverrError({
                        code: errors_1.ApiError.NETWORK_ERROR,
                        payload: error,
                    });
            }
        }
    }
    async getTransferOrderDetails(orderId) {
        try {
            return await this.get(`/v1/transfer-order-details/orderId/${orderId}`);
        }
        catch (error) {
            switch (error.response && error.response.status) {
                case 404:
                    commons_logs_1.log.info("getTransferOrderDetails: not found", orderId);
                    return undefined;
                default:
                    commons_logs_1.log.error("getTransferOrderDetails: unknown error", orderId, error.response);
                    throw new Error(errors_1.ApiError.UNKNOWN_ERROR);
            }
        }
    }
    async reportError(orderId, error) {
        try {
            await this.put("/v1/order/report-error", {
                orderId,
                error,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
    async batchQueueOrders(orderIds) {
        try {
            await this.put("/v1/order/batch-queue-orders", {
                orderIds,
            });
        }
        catch (error) {
            throw (0, base_client_1.mapError)(error);
        }
    }
}
exports.OrdersClient = OrdersClient;
//# sourceMappingURL=OrdersClient.js.map